import type { TagFilterSelectProps } from '@poseidon/components/Input2';
import { TagFilterSelect } from '@poseidon/components/Input2';

import { updateWithToast } from '@/clientOnlyUtils/clientUtils';
import { useTagCreateMutation } from '@/hooks/trpc/useTagMutation';
import { api } from '@/utils/api';

type TagFilterSelectNextJSProps = Omit<TagFilterSelectProps, 'tagOptions' | 'onCreate'> & {
  teamId: string;
  /** Allow creating new tags inline. Defaults to false. */
  allowCreate?: boolean;
  /** Only show tags marked as contactOnly. Defaults to false (show all tags). */
  contactOnly?: boolean;
};

export const TagFilterSelectNextJS = ({
  teamId,
  onChange,
  allowCreate,
  contactOnly,
  ...props
}: TagFilterSelectNextJSProps) => {
  const tagsQuery = api.tag.all.useQuery({ teamId });
  const createTagMutation = useTagCreateMutation();

  const tagOptions =
    tagsQuery.data?.tags
      .filter((tag) => !contactOnly || tag.contactOnly)
      .map((tag) => ({
        value: tag.id,
        label: tag.name,
      })) ?? [];

  const handleCreate = async (name: string) => {
    await updateWithToast({
      loading: 'Creating tag...',
      success: `Tag "${name}" created`,
      mutationFnAsync: async () => {
        const result = await createTagMutation.mutateAsync({
          teamId,
          data: { name, contactOnly: contactOnly ?? false },
        });
        // Select the newly created tag
        onChange(result.id);
      },
    });
  };

  return (
    <TagFilterSelect
      tagOptions={tagOptions}
      onChange={onChange}
      onCreate={allowCreate ? handleCreate : undefined}
      {...props}
    />
  );
};

export default TagFilterSelectNextJS;
